import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, BookOpen, CreditCard, AlertTriangle, Scale } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

const TermsPage: React.FC = () => {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Terms of Service | Flash-Read',
    description: 'Flash-Read Terms of Service. Read our terms governing use of the Flash-Read speed reading app, subscriptions, content licenses, and user conduct policies.',
  });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Home
          </button>
           <h1 className="text-4xl font-bold mb-4 text-center">
             <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
               Terms of Service
             </span>
           </h1>
           <p className="text-lg text-slate-600 dark:text-slate-400 text-center">Last updated: April 6, 2026</p>
        </div>

        {/* Introduction */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 mb-8 border border-slate-100 dark:border-slate-800">
          <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
            Welcome to Flash-Read. These Terms of Service ("Terms") govern your access to and use of the Flash-Read web application, including all features, content, and services provided by Flash-Read ("we," "our," or "us"). By accessing or using Flash-Read, you agree to be bound by these Terms. If you do not agree to these Terms, you may not use the service.
          </p>
        </div>

        {/* Table of Contents */}
        <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl p-6 mb-8 border border-slate-100 dark:border-slate-800">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Table of Contents</h2>
          <ol className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
            <li><a href="#acceptance" className="hover:text-primary transition-colors">1. Acceptance of Terms</a></li>
            <li><a href="#description" className="hover:text-primary transition-colors">2. Service Description</a></li>
            <li><a href="#accounts" className="hover:text-primary transition-colors">3. Accounts and Registration</a></li>
            <li><a href="#licenses" className="hover:text-primary transition-colors">4. Content Licenses and Restrictions</a></li>
            <li><a href="#subscriptions" className="hover:text-primary transition-colors">5. Subscriptions and Payments</a></li>
            <li><a href="#conduct" className="hover:text-primary transition-colors">6. User Conduct</a></li>
            <li><a href="#ip" className="hover:text-primary transition-colors">7. Intellectual Property</a></li>
            <li><a href="#disclaimers" className="hover:text-primary transition-colors">8. Disclaimers and Limitations</a></li>
            <li><a href="#termination" className="hover:text-primary transition-colors">9. Termination</a></li>
            <li><a href="#changes" className="hover:text-primary transition-colors">10. Changes to Terms</a></li>
            <li><a href="#contact" className="hover:text-primary transition-colors">11. Contact Information</a></li>
          </ol>
        </div>

        {/* Section 1 */}
        <section id="acceptance" className="mb-10">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">1. Acceptance of Terms</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              By accessing, browsing, or using Flash-Read, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. These Terms apply to all visitors, users, registered account holders, and subscribers of the service.
            </p>
            <p>
              If you are using Flash-Read on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms.
            </p>
          </div>
        </section>

        {/* Section 2 */}
        <section id="description" className="mb-10">
          <div className="flex items-center mb-4">
            <BookOpen className="h-6 w-6 text-purple-600 dark:text-purple-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">2. Service Description</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              Flash-Read is a gamified speed reading application that uses Rapid Serial Visual Presentation (RSVP) technology to display text one word at a time at configurable speeds ranging from 200 to 2,000 words per minute (WPM). The service includes:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>A built-in library of 500+ original stories across 20 genres</li>
              <li>Speed ramp training that gradually increases reading speed</li>
              <li>Progress tracking, XP system, achievements, and reading streaks</li>
              <li>Custom text import functionality (.txt and .epub files)</li>
              <li>Guest access with 3 free stories from the library</li>
              <li>Paid subscription tiers with full access to all features</li>
            </ul>
          </div>
        </section>

        {/* Section 3 */}
        <section id="accounts" className="mb-10">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-emerald-600 dark:text-emerald-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">3. Accounts and Registration</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              <strong>Guest Access:</strong> You may use Flash-Read as a guest without creating an account. Guest users can read 3 free stories from the library. After this limit, you must create an account to continue using the service.
            </p>
            <p>
              <strong>Registered Accounts:</strong> To access the full library, save reading progress, earn XP, and unlock achievements, you must create an account. You agree to provide accurate and complete information during registration and to keep your account information current.
            </p>
            <p>
              <strong>Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Notify us immediately of any unauthorized use of your account.
            </p>
            <p>
              <strong>Age Requirement:</strong> You must be at least 13 years old to use Flash-Read. Users under 18 require parental or guardian consent.
            </p>
          </div>
        </section>

        {/* Section 4 */}
        <section id="licenses" className="mb-10">
          <div className="flex items-center mb-4">
            <BookOpen className="h-6 w-6 text-orange-600 dark:text-orange-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">4. Content Licenses and Restrictions</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              <strong>Built-in Content:</strong> All stories and content provided in the Flash-Read library are original works owned by Flash-Read. They are protected by copyright and international intellectual property laws. You are granted a limited, non-exclusive, non-transferable license to access and read this content for personal, non-commercial use only.
            </p>
            <p>
              <strong>Restrictions:</strong> You may not reproduce, distribute, modify, create derivative works from, publicly display, or transmit any Flash-Read content without prior written permission. This includes but is not limited to copying, scraping, or extracting stories for use with other applications or services.
            </p>
            <p>
              <strong>User-Uploaded Content:</strong> You may upload your own text files (.txt, .epub) for use with the RSVP reader. You retain all rights to your uploaded content. By uploading content, you grant Flash-Read a limited license to process and display that content solely for your personal use within the application.
            </p>
          </div>
        </section>

        {/* Section 5 */}
        <section id="subscriptions" className="mb-10">
          <div className="flex items-center mb-4">
            <CreditCard className="h-6 w-6 text-pink-600 dark:text-pink-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">5. Subscriptions and Payments</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              <strong>Subscription Tiers:</strong> Flash-Read offers the following paid plans:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Starter</strong> — $4.49/month</li>
              <li><strong>Reader</strong> — $6.99/month</li>
              <li><strong>Ultra</strong> — $9.99/month</li>
            </ul>
            <p>
              <strong>Billing:</strong> Subscriptions are billed monthly through our payment processor (Stripe). By subscribing, you authorize recurring charges to your payment method on file.
            </p>
            <p>
              <strong>Cancellation:</strong> You may cancel your subscription at any time. Your access to paid features will continue until the end of your current billing period. No refunds are provided for partial billing periods.
            </p>
            <p>
              <strong>Price Changes:</strong> We reserve the right to modify subscription prices. Existing subscribers will be notified at least 30 days before any price change takes effect.
            </p>
          </div>
        </section>

        {/* Section 6 */}
        <section id="conduct" className="mb-10">
          <div className="flex items-center mb-4">
            <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">6. User Conduct</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              You agree not to use Flash-Read to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Upload content that is illegal, infringing, defamatory, obscene, or harmful</li>
              <li>Attempt to reverse engineer, decompile, or disassemble any part of the service</li>
              <li>Use automated tools (bots, scrapers, crawlers) to access or extract content from the service</li>
              <li>Interfere with or disrupt the integrity or performance of the service</li>
              <li>Share your account credentials or allow others to access your paid subscription</li>
              <li>Circumvent any access restrictions, session limits, or feature gating mechanisms</li>
            </ul>
          </div>
        </section>

        {/* Section 7 */}
        <section id="ip" className="mb-10">
          <div className="flex items-center mb-4">
            <Scale className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">7. Intellectual Property</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              Flash-Read and its original content, features, functionality, design, and branding are owned by Flash-Read and are protected by copyright, trademark, and other intellectual property laws. The Flash-Read name, logo, and related marks are trademarks of Flash-Read and may not be used without prior written permission.
            </p>
            <p>
              All stories in the Flash-Read library are original works. © 2026 Flash-Read. All rights reserved.
            </p>
          </div>
        </section>

        {/* Section 8 */}
        <section id="disclaimers" className="mb-10">
          <div className="flex items-center mb-4">
            <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">8. Disclaimers and Limitations</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              <strong>No Warranty:</strong> FLASH-READ IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              <strong>Speed Claims:</strong> Reading speed improvements vary by individual. While many users experience significant speed increases, results depend on factors including practice frequency, reading material complexity, and individual cognitive differences. Flash-Read does not guarantee specific WPM outcomes.
            </p>
            <p>
              <strong>Limitation of Liability:</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, FLASH-READ SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF OR INABILITY TO USE THE SERVICE.
            </p>
          </div>
        </section>

        {/* Section 9 */}
        <section id="termination" className="mb-10">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-slate-600 dark:text-slate-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">9. Termination</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              We may suspend or terminate your access to Flash-Read at any time, with or without cause, and with or without notice. Upon termination, your right to use the service will cease immediately. Provisions that by their nature should survive termination (including intellectual property, disclaimers, and limitations of liability) will survive.
            </p>
          </div>
        </section>

        {/* Section 10 */}
        <section id="changes" className="mb-10">
          <div className="flex items-center mb-4">
            <Scale className="h-6 w-6 text-slate-600 dark:text-slate-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">10. Changes to Terms</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              We may revise these Terms from time to time. The most current version will always be available on this page with an updated revision date. For material changes, we will notify users via email or through a prominent notice within the application at least 30 days before the changes take effect.
            </p>
          </div>
        </section>

        {/* Section 11 */}
        <section id="contact" className="mb-10">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">11. Contact Information</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              If you have questions about these Terms of Service, please contact us through the application or via our support channels.
            </p>
          </div>
        </section>

      </div>
    </div>
  );
};

export default TermsPage;
